import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { assignableRoles, canSignInWithPin, type Role } from '@lensmira/domain'
import { formatDay } from '@lensmira/i18n'

import { useTranslation } from '../i18n.js'
import { FormErrors, type FieldError } from './FormErrors.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { roleLabel } from '../status-labels.js'
import { PinSetup } from './PinSetup.js'
import { StockLocations } from './StockLocations.js'
import type { StockLocationOption } from './Purchasing.js'

/**
 * Hiring somebody, and opening a branch. §6.1 `TEN-002a`, §6.31 `ROLE-002`, `BR-01`, `ROLE-001a`.
 *
 * **A shop could be seeded and then it was frozen.** `staff`, `credential` and `branch` were each
 * written in exactly one place outside tests, all three in `tools/seed`, so hiring somebody,
 * opening a second branch or replacing a person who left all needed a developer with a psql
 * prompt. This is the screen for the two of those three that a shop does routinely.
 *
 * **The role chooser offers only roles this person may assign.** `assignableRoles` reads that off
 * Appendix B: a role may be assigned only if everything it can do, the assigner can already do.
 * Offering the rest and letting the server refuse would be a chooser whose choices are 403s, and a
 * 403 stops the write queue (`OFF-001a`) with everything behind it.
 *
 * **Branches are tick boxes and cost visibility is a second tick inside each.** `ROLE-001a` makes
 * them separate grants and defaults the second to off, so the form says so rather than implying
 * that working somewhere means seeing what things cost there.
 *
 * **The password is shown while it is typed, deliberately.** It is being set for somebody else and
 * read out to them across a counter, so a masked field here produces a password nobody can repeat
 * and a shop that writes it on paper instead.
 */

/** One person at the shop, as `GET /staff` gives them. */
export interface StaffRow {
  readonly id: string
  readonly displayName: string
  readonly role: string
  readonly branchIds: readonly string[]
  /** `BR-12`: a separate grant from the branch itself, so the panel can show and change it. */
  readonly costVisibleBranchIds: readonly string[]
  readonly offboardedAt: string | null
  /** `TEN-006`: their short name for PIN sign-in, or null. Optional for an older server. */
  readonly pinUsername?: string | null
  /** `ARC-012`'s lock, sent back with a change so a stale screen is a conflict, not an overwrite. */
  readonly version: number
}

export interface ShopSetupProps {
  /** The role of the person using the screen, which decides what they may assign. */
  readonly role: string
  /** The branches they may grant, which `BR-01` limits to the ones they hold themselves. */
  readonly branches: readonly { readonly id: string; readonly name: string }[]
  /** Where this person may see cost price, which is the only place they may grant it (`BR-12`). */
  readonly costVisibleBranchIds: readonly string[]
  /** Appendix B's `Branch - create, close`, owner only. */
  readonly canOpenBranch: boolean
  /** `tenant_settings_policies`: `OFF-003` makes the block size a shop policy, not a till's. */
  readonly canSetPolicies: boolean
  /**
   * `TEN-005` as `decisions/0014` settled it: whether this shop asks owners and managers to enrol
   * an authenticator before they can sign in.
   *
   * The shop's current answer, so the tick box shows a state rather than starting blank and
   * implying off. Off is the default for a new shop, and a blank tick box that reads as off while
   * the setting is on is how somebody turns a security policy off by accident.
   */
  readonly requireTotp: boolean
  /**
   * This person's own sign-in address and whether they already have an authenticator, for the
   * panel that lets them set one up. `TEN-005`, `decisions/0014`.
   *
   * **The shop asked for exactly this**: "if someone wants it just let them enable in the
   * settings". Enrolment existed only on the sign-in screen, which is reached by somebody who
   * cannot get in rather than by somebody who has decided they want a second factor.
   *
   * Absent when an older server sends neither, in which case the panel is not offered rather than
   * offered with a blank address.
   */
  readonly ownLoginIdentifier?: string
  readonly ownTotpEnrolled?: boolean
  /**
   * Enrol an authenticator for the caller, against their own password.
   *
   * Passed in rather than called from here, because this is the one write on the screen that does
   * not go through the write queue, and the reason is `OFF-001a`'s own: the queue exists so a sale
   * survives a dead network, and a secret that can only be shown once must never be replayed from
   * a queue minutes later onto a screen nobody is watching. Absent means the shell has no
   * configuration for it, same shape as `loadStaff`.
   */
  readonly enrolOwnTotp?: (password: string) => Promise<
    | { readonly kind: 'enrolled'; readonly secret: string; readonly uri: string }
    | { readonly kind: 'already_enrolled' }
    | { readonly kind: 'rejected' }
    | { readonly kind: 'unavailable' }
  >
  /** The branch being set up, which is the one an invoice number block belongs to (`BR-01`). */
  readonly branchId: string
  /**
   * Where stock may be put at this branch. §6.30 `BR-04`, `BR-10`; §6.10 `REF-010`.
   *
   * **A panel here rather than its own rail section**, for two reasons. A stock location is branch
   * structure and takes `branch_create_close`, the same owner-only row that opens a branch, which
   * is what this screen already is. And the rail has no free glyph: all 26 letters are taken, and
   * `rail.test.ts` enforces that they are distinct.
   */
  readonly loadLocations: () => Promise<readonly StockLocationOption[]>
  /** Appendix B's `Staff - offboard`, owner only, and narrower than hiring on purpose. */
  readonly canOffboard: boolean
  /**
   * Appendix B's `Staff - create, assign role`, which governs changing one and resetting a
   * password too: the server takes the same row for all three, because the act is the same one,
   * deciding what somebody may do.
   *
   * Separate from `canOpenBranch` and `canOffboard` so the screen can show a manager the people
   * list and the change panel while keeping the two owner-only acts off it.
   */
  readonly canManagePeople: boolean
  /** The people already here. Absent means the shell has no reads config. */
  readonly loadStaff?: () => Promise<readonly StaffRow[]>
  /** This person's own id, so the list can say which row is them and not offer to remove it. */
  readonly staffId: string
  /**
   * The current branch's timezone, for `LOC-004`. The offboarding date was printed as a raw ISO
   * day.
   *
   * Named apart from the `timezone` field in the new-branch form below, which is a value somebody
   * is typing for a branch that does not exist yet. The two are never the same thing.
   */
  readonly branchTimezone: string
  /**
   * And the rest of what the branch the owner is standing in trades under, as the opening offer
   * for the next one.
   *
   * The form asked somebody to type `Asia/Karachi` by hand, under its own warning that a wrong
   * timezone "puts the first hours of the morning on yesterday and nobody notices until a month
   * end". A second branch of a Lahore shop is in Pakistan, on Karachi time, selling in rupees, and
   * the shell already knew all three. Offered and editable, not fixed: `ACC-005` allows a branch
   * in another currency and §13 allows another country pack, so this is a default and not a rule.
   */
  readonly branchCountryCode: string
  readonly branchCurrency: string
  /**
   * `TEN-006`: digits in a PIN, or null/absent when the deployment has PIN sign-in off. The PIN
   * door is offered only when this is a number, because the server answers a PIN on a deployment
   * with none configured with a 409, and a 409 halts the write queue.
   */
  readonly pinLength?: number | null
  readonly onWritten?: () => void
}

export function ShopSetup({
  role,
  branches,
  costVisibleBranchIds,
  canOpenBranch,
  canSetPolicies,
  requireTotp,
  ownLoginIdentifier,
  ownTotpEnrolled,
  enrolOwnTotp,
  branchId,
  loadLocations,
  canOffboard,
  canManagePeople,
  loadStaff,
  staffId,
  branchTimezone,
  branchCountryCode,
  branchCurrency,
  pinLength,
  onWritten,
}: ShopSetupProps): JSX.Element {
  const { t, locale } = useTranslation()
  const displayDay = (value: string): string => formatDay(value, branchTimezone, locale)
  const { enqueue, snapshot } = useWriteQueue()
  const [displayName, setDisplayName] = useState('')
  const [newRole, setNewRole] = useState('')
  const [loginIdentifier, setLoginIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [granted, setGranted] = useState<readonly string[]>([])
  const [costAt, setCostAt] = useState<readonly string[]>([])
  /**
   * The first invoice number block. `OFF-003`.
   *
   * Prefilled with what a shop almost always wants: this branch's own series code and the fiscal
   * year `ACC-007a` is in. The size is deliberately blank, because it is the one thing nobody can
   * answer for them.
   */
  /**
   * The branch's invoice series template. `TAX-002`.
   *
   * The sibling of the block size and the other thing New order refuses to open without. Blank,
   * because `App.tsx` says why it will not default: "inventing a format would put a made-up number
   * on a tax invoice."
   */
  const [invoiceSeries, setInvoiceSeries] = useState('')
  /** `decisions/0014`: this person setting up their own authenticator. */
  const [ownPassword, setOwnPassword] = useState('')
  const [ownSecret, setOwnSecret] = useState<{ secret: string; uri: string } | null>(null)
  const [ownMessage, setOwnMessage] = useState<string | null>(null)
  const [ownBusy, setOwnBusy] = useState(false)
  const [blockTerminal, setBlockTerminal] = useState('')
  const [blockSeries, setBlockSeries] = useState('')
  const [blockYear, setBlockYear] = useState('')
  const [blockSize, setBlockSize] = useState('')
  const [branchName, setBranchName] = useState('')
  const [branchCode, setBranchCode] = useState('')
  // Prefilled from the branch the owner is in, and editable. See `branchCountryCode`.
  const [countryCode, setCountryCode] = useState(branchCountryCode)
  const [timezone, setTimezone] = useState(branchTimezone)
  const [currency, setCurrency] = useState(branchCurrency)
  const [staff, setStaff] = useState<readonly StaffRow[] | null>(null)
  const [staffFailed, setStaffFailed] = useState(false)
  const [confirming, setConfirming] = useState<string | null>(null)
  /** Which person's change panel is open, and what it holds. One at a time, like the confirm. */
  const [changing, setChanging] = useState<string | null>(null)
  const [changeRole, setChangeRole] = useState('')
  const [changeBranches, setChangeBranches] = useState<readonly string[]>([])
  const [changeCost, setChangeCost] = useState<readonly string[]>([])
  const [resetting, setResetting] = useState<string | null>(null)
  const [newPassword, setNewPassword] = useState('')
  const [reason, setReason] = useState('')
  const [writeId, setWriteId] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  /** §16.5's summary: every field that failed, not the first. */
  const [fieldErrors, setFieldErrors] = useState<readonly FieldError[]>([])

  const roles = assignableRoles(role as Role)

  /**
   * `BR-01`, pre-checked: somebody who works at a branch this caller does not hold is not theirs
   * to change or to give a password to.
   *
   * The server refuses it with `BRANCH_NOT_PERMITTED`, a 403, and a 403 is final to the write
   * queue, so a door that leads to one halts the till. `branches` is already what this session
   * holds: `session-context.service.ts` reads them `where id in (auth.branchIds)`.
   *
   * Every branch they work at, not any, which is the server's rule: one branch the caller does not
   * hold makes this somebody else's person.
   */
  const reachableHere = (person: StaffRow): boolean =>
    person.branchIds.every((id) => branches.some((branch) => branch.id === id))

  /**
   * Whether the change panel holds anything the server would act on.
   *
   * `NOTHING_TO_CHANGE` is a 422 and a refused write holds every write queued behind it, so a
   * button that sends an unchanged row would stop the till for a press that meant nothing.
   */
  const somethingChanged = (person: StaffRow): boolean => {
    const same = (a: readonly string[], b: readonly string[]): boolean =>
      a.length === b.length && [...a].sort().every((id, i) => id === [...b].sort()[i])
    return (
      changeRole !== person.role ||
      !same(changeBranches, person.branchIds) ||
      !same(changeCost, person.costVisibleBranchIds)
    )
  }

  const refresh = useCallback(() => {
    if (loadStaff === undefined) return
    void loadStaff()
      .then((loaded) => {
        setStaff(loaded)
        setStaffFailed(false)
      })
      .catch(() => {
        // Its own state, not an empty list: "nobody works here" is never true of a shop somebody is
        // signed in to, and showing that sentence beside the error would be two answers to one
        // question.
        setStaffFailed(true)
        setError(t('shopSetup.staffLoadFailed'))
      })
  }, [loadStaff, t])

  useEffect(() => {
    refresh()
  }, [refresh])

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setConfirming(null)
    setReason('')
    refresh()
    setDisplayName('')
    setLoginIdentifier('')
    setPassword('')
    setGranted([])
    setCostAt([])
    setBranchName('')
    setBranchCode('')
    onWritten?.()
  })

  const toggle = (
    list: readonly string[],
    set: (next: readonly string[]) => void,
    id: string,
  ): void => {
    set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id])
  }

  const addPerson = useCallback(async () => {
    /**
     * **Every failure, not the first one.** §16.5 asks for "a form error summary at the top
     * listing every failed field as a link", and this used to `return` at the first: a shopkeeper
     * who left the form blank was told about the name, filled it, was told about the role, and so
     * on, five submissions to learn what one glance could have said.
     *
     * The password check is here as well as on the server because a refused write blocks the queue
     * head, and because an empty password hashes perfectly well and makes an account anybody can
     * sign into.
     */
    const failures: FieldError[] = []
    if (displayName.trim() === '') {
      failures.push({
        fieldId: 'setup-person-name',
        label: t('shopSetup.nameLabel'),
        message: t('shopSetup.nameRequired'),
      })
    }
    if (newRole === '') {
      failures.push({
        fieldId: 'setup-person-role',
        label: t('shopSetup.roleLabel'),
        message: t('shopSetup.roleRequired'),
      })
    }
    if (loginIdentifier.trim() === '') {
      failures.push({
        fieldId: 'setup-person-login',
        label: t('shopSetup.loginLabel'),
        message: t('shopSetup.loginRequired'),
      })
    }
    if (password === '') {
      failures.push({
        fieldId: 'setup-person-password',
        label: t('shopSetup.passwordLabel'),
        message: t('shopSetup.passwordRequired'),
      })
    }
    if (granted.length === 0) {
      failures.push({
        fieldId: 'setup-person-branches',
        label: t('shopSetup.branchesLegend'),
        message: t('shopSetup.branchRequired'),
      })
    }
    setFieldErrors(failures)
    if (failures.length > 0) return

    setError(null)
    setDone(null)
    const write = (await enqueue({
      method: 'POST',
      path: '/staff',
      label: t('shopSetup.queuePersonLabel', { name: displayName.trim() }),
      body: {
        displayName: displayName.trim(),
        role: newRole,
        loginIdentifier: loginIdentifier.trim(),
        password,
        branchIds: [...granted],
        ...(costAt.length === 0 ? {} : { costVisibleBranchIds: [...costAt] }),
      },
    })) as { id: string }
    setWriteId(write.id)
    setDone(t('shopSetup.personQueued'))
  }, [costAt, displayName, enqueue, granted, loginIdentifier, newRole, password, t])

  const offboard = useCallback(
    async (person: StaffRow) => {
      setError(null)
      setDone(null)
      const write = (await enqueue({
        method: 'POST',
        path: `/staff/${encodeURIComponent(person.id)}/offboard`,
        label: t('shopSetup.queueOffboardLabel', { name: person.displayName }),
        body: {
          // `ROLE-004`'s "reason where required". Optional, and sent only when given: an empty
          // string in the audit row is worse than nothing, because it looks like an answer.
          ...(reason.trim() === '' ? {} : { reason: reason.trim() }),
        },
      })) as { id: string }
      setWriteId(write.id)
      setDone(t('shopSetup.offboardQueued', { name: person.displayName }))
    },
    [enqueue, reason, t],
  )

  /**
   * `PATCH /staff/:staffId`. §6.31 `ROLE-002`, `BR-01`, `BR-12`.
   *
   * Everything is sent, not a diff: the route replaces the grant set, and a panel that sent only
   * what it believed had changed would have to be right about what the list said when it opened.
   * `expectedVersion` is what makes that safe, and it comes from the row the screen was shown.
   *
   * The refusals this pre-checks are the ones it can see: `BRANCH_REQUIRED` (the chooser will not
   * let the last one go), `NOTHING_TO_CHANGE` (the button is dead until something differs) and
   * `ROLE_NOT_ASSIGNABLE` (the role list holds only roles this caller may assign). The rest,
   * `LAST_OWNER` and `VERSION_CONFLICT` among them, need the shop's state and are the server's.
   */
  const applyChange = useCallback(
    async (person: StaffRow) => {
      if (changeBranches.length === 0) {
        setError(t('shopSetup.changeNeedsBranch'))
        return
      }
      setError(null)
      setDone(null)
      const write = (await enqueue({
        method: 'PATCH',
        path: `/staff/${encodeURIComponent(person.id)}`,
        label: t('shopSetup.queueChangeLabel', { name: person.displayName }),
        body: {
          role: changeRole,
          branchIds: [...changeBranches],
          costVisibleBranchIds: [...changeCost],
          expectedVersion: person.version,
        },
      })) as { id: string }
      setWriteId(write.id)
      setDone(t('shopSetup.changeQueued', { name: person.displayName }))
      setChanging(null)
    },
    [changeBranches, changeCost, changeRole, enqueue, t],
  )

  /**
   * `POST /staff/:staffId/password`. §6.31 `ROLE-002`, `TEN-005`, `NFR-011`.
   *
   * Offered for other people only, because the route refuses the caller's own account: a reset
   * that asks for no current password must not be able to reach the session making the request.
   * Changing your own password needs the old one and is not this.
   */
  /**
   * Clearing somebody's authenticator. `TEN-005`, `decisions/0014`.
   *
   * **The lost-phone path.** Nothing ever wrote `credential.totp_secret` back to null, and
   * enrolling refuses while one is there, so an account whose authenticator was gone could neither
   * sign in nor re-enrol. Turning the shop's requirement off does not reach them: an enrolled
   * account is always asked for its code, whatever the policy says.
   *
   * No confirm step of its own, unlike offboarding. It takes nothing away that the holder can
   * still use, the server revokes their sessions, and the alternative is somebody standing at a
   * counter unable to work.
   */
  const clearTotp = useCallback(
    async (person: StaffRow) => {
      setError(null)
      setDone(null)
      const write = (await enqueue({
        method: 'POST',
        path: `/staff/${encodeURIComponent(person.id)}/totp/clear`,
        label: t('shopSetup.queueClearTotpLabel', { name: person.displayName }),
        body: {},
      })) as { id: string }
      setWriteId(write.id)
      setDone(t('shopSetup.clearTotpQueued', { name: person.displayName }))
    },
    [enqueue, t],
  )

  /**
   * Set up an authenticator on the caller's own account. `TEN-005`, `decisions/0014`.
   *
   * **Not through the write queue**, which every other write on this screen uses. `OFF-001a`'s
   * queue exists so a sale survives a dead network, and it is the wrong carrier for this: the
   * secret is returned exactly once and nothing can read it back, so a queued enrolment that
   * drains minutes later would show it to an empty screen and leave the account enrolled with a
   * secret nobody has. It is a synchronous request or it is nothing.
   *
   * **The password is asked for again**, which `TEN-005`'s "re-auth on privilege escalation"
   * covers: adding a second factor to an account is exactly that, and a screen left open at a
   * counter must not be enough on its own.
   */
  const enrolOwn = useCallback(async () => {
    if (enrolOwnTotp === undefined) return
    if (ownPassword === '') {
      setOwnMessage(t('shopSetup.myTotpNeedsPassword'))
      return
    }
    setOwnBusy(true)
    setOwnMessage(null)
    const result = await enrolOwnTotp(ownPassword)
    setOwnBusy(false)
    // Cleared whatever happened: a password that failed is one somebody retypes, and one that
    // worked has no reason to stay in a box on a shared counter screen.
    setOwnPassword('')
    switch (result.kind) {
      case 'enrolled':
        setOwnSecret({ secret: result.secret, uri: result.uri })
        return
      case 'already_enrolled':
        setOwnMessage(t('shopSetup.myTotpAlready'))
        return
      case 'unavailable':
        setOwnMessage(t('shopSetup.myTotpUnreachable'))
        return
      case 'rejected':
        setOwnMessage(t('shopSetup.myTotpFailed'))
    }
  }, [enrolOwnTotp, ownPassword, t])

  const resetPassword = useCallback(
    async (person: StaffRow) => {
      if (newPassword === '') {
        setError(t('shopSetup.newPasswordRequired'))
        return
      }
      setError(null)
      setDone(null)
      const write = (await enqueue({
        method: 'POST',
        path: `/staff/${encodeURIComponent(person.id)}/password`,
        label: t('shopSetup.queueResetLabel', { name: person.displayName }),
        body: { password: newPassword },
      })) as { id: string }
      setWriteId(write.id)
      setDone(t('shopSetup.resetQueued', { name: person.displayName }))
      // Closing the panel is what takes the box away, and opening one clears it. Clearing here as
      // well looked like protection and was not: a mutation that deleted it changed nothing, so
      // the line was a claim the code did not make.
      setResetting(null)
    },
    [enqueue, newPassword, t],
  )

  /**
   * The first block for a terminal, which is what stands between a new shop and its first sale.
   *
   * `confirm` refuses with `NO_NUMBER_BLOCK` until one exists, and every later block is sized from
   * this one, so a shop answers it once per till and never again. The year is asked for because
   * `ACC-007a` runs July to June and a shop setting up in May is choosing which year it is
   * starting in.
   */
  const saveInvoiceSeries = async (): Promise<void> => {
    const template = invoiceSeries.trim()
    // The two the number cannot be built without, checked here so a typo is a sentence rather than
    // a 422 that stops the queue (`OFF-001a`).
    if (!template.includes('{series}') || !template.includes('{seq')) {
      setError(t('shopSetup.seriesInvalid'))
      return
    }
    setError(null)
    setDone(null)
    const write = (await enqueue({
      method: 'PATCH',
      path: `/branches/${branchId}`,
      label: t('shopSetup.queueSeriesLabel'),
      body: { invoiceSeries: template },
    })) as { id: string }
    setWriteId(write.id)
  }

  /**
   * Turn the authenticator requirement on or off. `TEN-005`, `decisions/0014`.
   *
   * **Sends the value it wants, not a toggle.** A queued write can be retried, and `OFF-001a`
   * means it may be sent long after the tick was made; "flip it" replayed twice leaves the policy
   * where it started. The idempotency key makes the retry safe either way, and a write that states
   * its intent is safe without needing it to.
   */
  const saveRequireTotp = async (next: boolean): Promise<void> => {
    setError(null)
    setDone(null)
    const write = (await enqueue({
      method: 'PATCH',
      path: '/settings/require-totp',
      label: next ? t('shopSetup.queueTotpOnLabel') : t('shopSetup.queueTotpOffLabel'),
      body: { requireTotp: next },
    })) as { id: string }
    setWriteId(write.id)
  }

  const allocateBlock = async (): Promise<void> => {
    const size = Number(blockSize.trim())
    const year = Number(blockYear.trim())
    if (blockTerminal.trim() === '') {
      setError(t('shopSetup.blockTerminalRequired'))
      return
    }
    if (blockSeries.trim() === '') {
      setError(t('shopSetup.blockSeriesRequired'))
      return
    }
    if (!Number.isInteger(year) || year <= 0) {
      setError(t('shopSetup.blockYearRequired'))
      return
    }
    if (!Number.isInteger(size) || size <= 0) {
      setError(t('shopSetup.blockSizeRequired'))
      return
    }

    setError(null)
    setDone(null)
    const write = (await enqueue({
      method: 'POST',
      path: '/orders/invoice-number-blocks',
      label: t('shopSetup.queueBlockLabel', { terminal: blockTerminal.trim() }),
      body: {
        branchId,
        terminalId: blockTerminal.trim(),
        seriesCode: blockSeries.trim(),
        fiscalYear: year,
        size,
      },
    })) as { id: string }
    setWriteId(write.id)
  }

  const openBranch = useCallback(async () => {
    for (const [value, key] of [
      [branchName, 'shopSetup.branchNameRequired'],
      [branchCode, 'shopSetup.branchCodeRequired'],
      [countryCode, 'shopSetup.countryRequired'],
      [timezone, 'shopSetup.timezoneRequired'],
      [currency, 'shopSetup.currencyRequired'],
    ] as const) {
      if (value.trim() === '') {
        setError(t(key))
        return
      }
    }

    setError(null)
    setDone(null)
    const write = (await enqueue({
      method: 'POST',
      path: '/branches',
      label: t('shopSetup.queueBranchLabel', { name: branchName.trim() }),
      body: {
        name: branchName.trim(),
        code: branchCode.trim(),
        countryCode: countryCode.trim(),
        timezone: timezone.trim(),
        currency: currency.trim(),
      },
    })) as { id: string }
    setWriteId(write.id)
    setDone(t('shopSetup.branchQueued'))
  }, [branchCode, branchName, countryCode, currency, enqueue, t, timezone])

  const line = statusLine(t, snapshot, writeId, t('queue.subject.setup'))

  return (
    <section className="lm-screen" aria-labelledby="shop-setup-heading">
      <h1 id="shop-setup-heading" className="lm-screen__heading">
        {t('shopSetup.heading')}
      </h1>

      <section className="lm-setup-panel" aria-labelledby="people-heading">
        <h2 id="people-heading" className="lm-setup-panel__heading">
          {t('shopSetup.peopleHeading')}
        </h2>
        {staffFailed ? null : staff === null ? (
          <p className="lm-hint">{t('shopSetup.peopleLoading')}</p>
        ) : staff.length === 0 ? (
          <p className="lm-hint">{t('shopSetup.peopleNone')}</p>
        ) : (
          <ul className="lm-setup-panel__people">
            {staff.map((person) => (
              <li key={person.id} className="lm-setup-person">
                <span className="lm-setup-person__name" dir="auto">
                  {person.displayName}
                </span>
                <span className="lm-setup-person__meta">
                  {roleLabel(t, person.role)}
                  {person.offboardedAt === null
                    ? ''
                    : ` · ${t('shopSetup.offboardedOn', { date: displayDay(person.offboardedAt) })}`}
                  {person.id === staffId ? ` · ${t('shopSetup.thatIsYou')}` : ''}
                </span>
                {/* The two acts between hiring and offboarding, which had no route and no screen
                    until now: changing what somebody may do, and giving them a new password.

                    Offered only where they can succeed. The server refuses the caller's own
                    account for both (`CANNOT_CHANGE_YOUR_OWN_ROLE`, `CANNOT_RESET_YOUR_OWN`),
                    somebody who has left (`ALREADY_OFFBOARDED`), and a role this caller could not
                    assign (`ROLE_NOT_ASSIGNABLE`). Each of those is a 403 or a 409, both final to
                    the write queue, so a door that leads to one halts the till. */}
                {canManagePeople &&
                person.offboardedAt === null &&
                person.id !== staffId &&
                roles.includes(person.role as Role) &&
                reachableHere(person) ? (
                  changing === person.id ? (
                    <div className="lm-setup-person__change">
                      <label className="lm-field">
                        <span className="lm-field__label">{t('shopSetup.changeRole')}</span>
                        <select
                          className="lm-input"
                          value={changeRole}
                          onChange={(e) => setChangeRole(e.currentTarget.value)}
                        >
                          {roles.map((option) => (
                            <option key={option} value={option}>
                              {roleLabel(t, option)}
                            </option>
                          ))}
                        </select>
                      </label>
                      <fieldset className="lm-field">
                        <legend className="lm-field__label">{t('shopSetup.changeBranches')}</legend>
                        {branches.map((branch) => (
                          <label key={branch.id} className="lm-field lm-field--inline">
                            <input
                              type="checkbox"
                              checked={changeBranches.includes(branch.id)}
                              onChange={(e) => {
                                // Read now, not inside the updater: `currentTarget` is only the
                                // element while the handler is on the stack, and React runs a
                                // functional update later, by which time it is null.
                                const granting = e.currentTarget.checked
                                setChangeBranches((was) =>
                                  granting
                                    ? [...was, branch.id]
                                    : was.filter((id) => id !== branch.id),
                                )
                                // A branch they no longer work at cannot keep its cost visibility:
                                // `COST_BRANCH_NOT_GRANTED` is the server's refusal for exactly
                                // that, and it is a 422.
                                if (!granting) {
                                  setChangeCost((was) => was.filter((id) => id !== branch.id))
                                }
                              }}
                            />
                            <span>{branch.name}</span>
                          </label>
                        ))}
                      </fieldset>
                      {/* `BR-12`, offered only where this caller has it themselves, which is the
                          server's `COST_VISIBILITY_NOT_YOURS`. A grant already held is shown and
                          can be taken away from anywhere: revoking needs no visibility of your
                          own, which is what the route says too. */}
                      <fieldset className="lm-field">
                        <legend className="lm-field__label">{t('shopSetup.changeCost')}</legend>
                        {branches
                          .filter(
                            (branch) =>
                              changeBranches.includes(branch.id) &&
                              (costVisibleBranchIds.includes(branch.id) ||
                                person.costVisibleBranchIds.includes(branch.id)),
                          )
                          .map((branch) => (
                            <label key={branch.id} className="lm-field lm-field--inline">
                              <input
                                type="checkbox"
                                checked={changeCost.includes(branch.id)}
                                onChange={(e) => {
                                  const granting = e.currentTarget.checked
                                  setChangeCost((was) =>
                                    granting
                                      ? [...was, branch.id]
                                      : was.filter((id) => id !== branch.id),
                                  )
                                }}
                              />
                              <span>{branch.name}</span>
                            </label>
                          ))}
                      </fieldset>
                      <p className="lm-setup-panel__effects">{t('shopSetup.changeMeans')}</p>
                      <button
                        type="button"
                        className="lm-button lm-button--primary"
                        disabled={writeId !== null || !somethingChanged(person)}
                        onClick={() => {
                          void applyChange(person)
                        }}
                      >
                        {t('shopSetup.changeConfirm')}
                      </button>
                      <button
                        type="button"
                        className="lm-button lm-button--secondary"
                        onClick={() => setChanging(null)}
                      >
                        {t('shopSetup.changeCancel')}
                      </button>
                    </div>
                  ) : resetting === person.id ? (
                    <div className="lm-setup-person__change">
                      <label className="lm-field">
                        <span className="lm-field__label">{t('shopSetup.newPassword')}</span>
                        <input
                          className="lm-input"
                          type="password"
                          value={newPassword}
                          autoComplete="new-password"
                          onChange={(e) => setNewPassword(e.currentTarget.value)}
                        />
                      </label>
                      <p className="lm-setup-panel__effects">
                        {t('shopSetup.resetMeans', { name: person.displayName })}
                      </p>
                      <button
                        type="button"
                        className="lm-button lm-button--primary"
                        disabled={writeId !== null || newPassword === ''}
                        onClick={() => {
                          void resetPassword(person)
                        }}
                      >
                        {t('shopSetup.resetConfirm')}
                      </button>
                      <button
                        type="button"
                        className="lm-button lm-button--secondary"
                        onClick={() => {
                          setResetting(null)
                          setNewPassword('')
                        }}
                      >
                        {t('shopSetup.resetCancel')}
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="lm-button lm-button--secondary"
                        onClick={() => {
                          setResetting(null)
                          setChanging(person.id)
                          setChangeRole(person.role)
                          setChangeBranches([...person.branchIds])
                          setChangeCost([...person.costVisibleBranchIds])
                        }}
                      >
                        {t('shopSetup.change', { name: person.displayName })}
                      </button>
                      <button
                        type="button"
                        className="lm-button lm-button--secondary"
                        onClick={() => {
                          setChanging(null)
                          setResetting(person.id)
                          // Cleared on the way in, which is the clear that does the work: one box
                          // serves every row, so without it a password typed for one person and
                          // not sent would be sitting in the next person's panel.
                          setNewPassword('')
                        }}
                      >
                        {t('shopSetup.reset', { name: person.displayName })}
                      </button>
                      {/* `decisions/0014`: the only way back in for somebody whose authenticator
                          is gone. They re-enrol from the sign-in screen afterwards. */}
                      <button
                        type="button"
                        className="lm-button lm-button--secondary"
                        disabled={writeId !== null}
                        onClick={() => {
                          void clearTotp(person)
                        }}
                      >
                        {t('shopSetup.clearTotp', { name: person.displayName })}
                      </button>
                    </>
                  )
                ) : null}
                {/* `TEN-006`. The same door rules as the change and the reset above, plus the
                    two PIN adds: the deployment has PIN sign-in on (the server's 409 otherwise
                    halts the queue) and the role may hold one (owner and manager sign in with an
                    authenticator, and a PIN would skip it). */}
                {typeof pinLength === 'number' &&
                canManagePeople &&
                person.offboardedAt === null &&
                person.id !== staffId &&
                roles.includes(person.role as Role) &&
                canSignInWithPin(person.role) &&
                reachableHere(person) ? (
                  <PinSetup
                    staffId={person.id}
                    displayName={person.displayName}
                    pinUsername={person.pinUsername ?? null}
                    pinLength={pinLength}
                    onQueued={(id) => {
                      setError(null)
                      setWriteId(id)
                      setDone(t('shopSetup.pin.queued', { name: person.displayName }))
                    }}
                  />
                ) : null}
                {/* Offered only where it can succeed: the owner row, somebody who still works here,
                    and not the person holding the screen. The server refuses all three, and a
                    refusal stops the write queue. */}
                {canOffboard && person.offboardedAt === null && person.id !== staffId ? (
                  confirming === person.id ? (
                    <div className="lm-setup-person__confirm">
                      <p className="lm-setup-panel__effects">
                        {t('shopSetup.offboardMeans', { name: person.displayName })}
                      </p>
                      <label className="lm-field">
                        <span className="lm-field__label">{t('shopSetup.offboardReason')}</span>
                        <input
                          className="lm-input"
                          dir="auto"
                          value={reason}
                          autoComplete="off"
                          onChange={(e) => setReason(e.currentTarget.value)}
                        />
                      </label>
                      <button
                        type="button"
                        className="lm-button lm-button--danger"
                        disabled={writeId !== null}
                        onClick={() => {
                          void offboard(person)
                        }}
                      >
                        {t('shopSetup.offboardConfirm')}
                      </button>
                      <button
                        type="button"
                        className="lm-button lm-button--secondary"
                        onClick={() => {
                          setConfirming(null)
                          setReason('')
                        }}
                      >
                        {t('shopSetup.offboardCancel')}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="lm-button lm-button--secondary"
                      onClick={() => {
                        setConfirming(person.id)
                        setReason('')
                      }}
                    >
                      {t('shopSetup.offboard', { name: person.displayName })}
                    </button>
                  )
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="lm-setup-panel" aria-labelledby="add-person-heading">
        <h2 id="add-person-heading" className="lm-setup-panel__heading">
          {t('shopSetup.personHeading')}
        </h2>
        <p className="lm-setup-panel__effects">{t('shopSetup.personMeans')}</p>

        {/* At the top, because a sentence under the submit button about a field above the fold is
            a message somebody has to go hunting for. */}
        <FormErrors errors={fieldErrors} />

        <label className="lm-field">
          <span className="lm-field__label">{t('shopSetup.nameLabel')}</span>
          {/* `LOC-005`: a name takes its own direction inside whichever page it sits in. */}
          <input
            id="setup-person-name"
            className="lm-input"
            dir="auto"
            value={displayName}
            autoComplete="off"
            onChange={(e) => setDisplayName(e.currentTarget.value)}
          />
        </label>

        <label className="lm-field">
          <span className="lm-field__label">{t('shopSetup.roleLabel')}</span>
          <select
            id="setup-person-role"
            className="lm-input"
            value={newRole}
            onChange={(e) => setNewRole(e.currentTarget.value)}
          >
            {/* No default. A role decides what somebody may do with a shop's money, so it is
                chosen rather than pre-selected. */}
            <option value="">{t('shopSetup.roleUnset')}</option>
            {roles.map((option) => (
              <option key={option} value={option}>
                {roleLabel(t, option)}
              </option>
            ))}
          </select>
        </label>
        <p className="lm-hint">{t('shopSetup.roleLimit')}</p>

        <label className="lm-field">
          <span className="lm-field__label">{t('shopSetup.loginLabel')}</span>
          <input
            id="setup-person-login"
            className="lm-input"
            dir="ltr"
            value={loginIdentifier}
            autoComplete="off"
            onChange={(e) => setLoginIdentifier(e.currentTarget.value)}
          />
        </label>

        <label className="lm-field">
          <span className="lm-field__label">{t('shopSetup.passwordLabel')}</span>
          {/* Deliberately `text`: this is set for somebody else and read out to them. A masked
              field produces a password nobody can repeat and a shop that writes it on paper. */}
          <input
            id="setup-person-password"
            className="lm-input"
            type="text"
            dir="ltr"
            value={password}
            autoComplete="off"
            onChange={(e) => setPassword(e.currentTarget.value)}
          />
        </label>
        <p className="lm-field__warning">{t('shopSetup.passwordMeans')}</p>

        {/* `tabIndex` so the summary's link can land on it: a fieldset is not focusable, and a
            link to a branch list that moves focus nowhere is a link that does nothing. */}
        <fieldset id="setup-person-branches" className="lm-field" tabIndex={-1}>
          <legend className="lm-field__label">{t('shopSetup.branchesLegend')}</legend>
          {branches.map((branch) => (
            <div key={branch.id} className="lm-setup-panel__grant">
              <label>
                <input
                  type="checkbox"
                  checked={granted.includes(branch.id)}
                  onChange={() => toggle(granted, setGranted, branch.id)}
                />{' '}
                {branch.name}
              </label>
              {/* `ROLE-001a`: a separate grant, defaulting to off, and offered only where the
                  person doing the granting holds it themselves (`BR-12`). */}
              {granted.includes(branch.id) && costVisibleBranchIds.includes(branch.id) ? (
                <label className="lm-setup-panel__cost">
                  <input
                    type="checkbox"
                    checked={costAt.includes(branch.id)}
                    onChange={() => toggle(costAt, setCostAt, branch.id)}
                  />{' '}
                  {t('shopSetup.costAt', { branch: branch.name })}
                </label>
              ) : null}
            </div>
          ))}
        </fieldset>

        <button
          type="button"
          className="lm-button lm-button--primary"
          disabled={writeId !== null}
          onClick={() => {
            void addPerson()
          }}
        >
          {t('shopSetup.addPerson')}
        </button>
      </section>

      {canOpenBranch ? (
        <section className="lm-setup-panel" aria-labelledby="open-branch-heading">
          <h2 id="open-branch-heading" className="lm-setup-panel__heading">
            {t('shopSetup.branchHeading')}
          </h2>
          <p className="lm-setup-panel__effects">{t('shopSetup.branchMeans')}</p>

          <label className="lm-field">
            <span className="lm-field__label">{t('shopSetup.branchNameLabel')}</span>
            <input
              className="lm-input"
              dir="auto"
              value={branchName}
              autoComplete="off"
              onChange={(e) => setBranchName(e.currentTarget.value)}
            />
          </label>

          <label className="lm-field">
            <span className="lm-field__label">{t('shopSetup.branchCodeLabel')}</span>
            <input
              className="lm-input"
              dir="ltr"
              value={branchCode}
              autoComplete="off"
              onChange={(e) => setBranchCode(e.currentTarget.value)}
            />
          </label>

          <label className="lm-field">
            <span className="lm-field__label">{t('shopSetup.countryLabel')}</span>
            <input
              className="lm-input"
              dir="ltr"
              value={countryCode}
              autoComplete="off"
              onChange={(e) => setCountryCode(e.currentTarget.value)}
            />
          </label>

          <label className="lm-field">
            <span className="lm-field__label">{t('shopSetup.timezoneLabel')}</span>
            <input
              className="lm-input"
              dir="ltr"
              value={timezone}
              autoComplete="off"
              onChange={(e) => setTimezone(e.currentTarget.value)}
            />
          </label>
          {/* The branch day decides which day a sale belongs to, and the server refuses a zone it
              cannot parse rather than falling back to UTC. A sibling rather than a child, so the
              field's accessible name stays the field's name. */}
          <p className="lm-field__warning">{t('shopSetup.timezoneMeans')}</p>

          <label className="lm-field">
            <span className="lm-field__label">{t('shopSetup.currencyLabel')}</span>
            <input
              className="lm-input"
              dir="ltr"
              value={currency}
              autoComplete="off"
              onChange={(e) => setCurrency(e.currentTarget.value)}
            />
          </label>

          <button
            type="button"
            className="lm-button"
            disabled={writeId !== null}
            onClick={() => {
              void openBranch()
            }}
          >
            {t('shopSetup.openBranch')}
          </button>
        </section>
      ) : null}

      {/* `decisions/0014`: anybody, not only an owner. The shop's words were "if someone wants it
          just let them enable in the settings", and a second factor on your own account is not a
          shop policy. Offered whether or not `require_totp` is on. */}
      {enrolOwnTotp !== undefined && ownLoginIdentifier !== undefined ? (
        <section className="lm-setup-panel" aria-labelledby="own-totp-heading">
          <h2 id="own-totp-heading" className="lm-setup-panel__heading">
            {t('shopSetup.myTotpHeading')}
          </h2>
          <p className="lm-setup-panel__effects">{t('shopSetup.myTotpMeans')}</p>

          {ownTotpEnrolled === true ? (
            <p className="lm-setup-panel__effects" role="status">
              {t('shopSetup.myTotpOn')}
            </p>
          ) : ownSecret === null ? (
            <>
              <label className="lm-field">
                <span className="lm-field__label">{t('shopSetup.myTotpPassword')}</span>
                <input
                  className="lm-input"
                  type="password"
                  dir="ltr"
                  value={ownPassword}
                  autoComplete="current-password"
                  onChange={(e) => setOwnPassword(e.currentTarget.value)}
                />
              </label>
              <button
                type="button"
                className="lm-button lm-button--primary"
                disabled={ownBusy}
                onClick={() => {
                  void enrolOwn()
                }}
              >
                {t('shopSetup.myTotpEnrol')}
              </button>
            </>
          ) : (
            <>
              <span className="lm-field__label">{t('shopSetup.myTotpSecret')}</span>
              {/* Base32, which is Latin whatever the page direction and must not mirror. */}
              <code className="lm-enrol__secret" dir="ltr">
                {ownSecret.secret}
              </code>
              <a className="lm-enrol__link" href={ownSecret.uri}>
                {t('shopSetup.myTotpLink')}
              </a>
              <p className="lm-setup-panel__effects">{t('shopSetup.myTotpOnce')}</p>
            </>
          )}

          {ownMessage === null ? null : (
            <p className="lm-setup-panel__effects" role="status">
              {ownMessage}
            </p>
          )}
        </section>
      ) : null}

      {canSetPolicies ? (
        <section className="lm-setup-panel" aria-labelledby="totp-policy-heading">
          <h2 id="totp-policy-heading" className="lm-setup-panel__heading">
            {t('shopSetup.totpHeading')}
          </h2>
          {/* `TEN-005` was built as a product rule and locked the first owner of every new shop
              out of their own records. `decisions/0014` made it this tick box instead. */}
          <p className="lm-setup-panel__effects">{t('shopSetup.totpMeans')}</p>

          <label className="lm-field lm-field--inline">
            <input
              type="checkbox"
              checked={requireTotp}
              disabled={writeId !== null}
              onChange={(e) => {
                // Read before the await: `currentTarget` is only the element while the handler is
                // on the stack, which the branch tick boxes above learned the hard way.
                const next = e.currentTarget.checked
                void saveRequireTotp(next)
              }}
            />
            <span className="lm-field__label">{t('shopSetup.totpRequire')}</span>
          </label>

          {/* The one consequence somebody needs before ticking it, rather than after. Anybody
              already enrolled is unaffected; anybody who is not cannot sign in until they are. */}
          <p className="lm-setup-panel__effects">{t('shopSetup.totpWarning')}</p>
        </section>
      ) : null}

      {canSetPolicies ? (
        <section className="lm-setup-panel" aria-labelledby="number-block-heading">
          <h2 id="number-block-heading" className="lm-setup-panel__heading">
            {t('shopSetup.blockHeading')}
          </h2>
          {/* `OFF-003`: the size is the offline window in disguise, which is the only way to
              explain it to a shopkeeper that lets them answer it. */}
          <p className="lm-setup-panel__effects">{t('shopSetup.blockMeans')}</p>

          <label className="lm-field">
            <span className="lm-field__label">{t('shopSetup.seriesLabel')}</span>
            {/* `LOC-005`: a template is Latin whatever the page direction, and must not mirror. */}
            <input
              className="lm-input"
              dir="ltr"
              value={invoiceSeries}
              autoComplete="off"
              placeholder="{series}-{year}-{seq:6}"
              onChange={(e) => setInvoiceSeries(e.currentTarget.value)}
            />
          </label>

          <button
            type="button"
            className="lm-button"
            disabled={writeId !== null}
            onClick={() => {
              void saveInvoiceSeries()
            }}
          >
            {t('shopSetup.saveSeries')}
          </button>

          <label className="lm-field">
            <span className="lm-field__label">{t('shopSetup.blockTerminalLabel')}</span>
            <input
              className="lm-input"
              dir="ltr"
              value={blockTerminal}
              autoComplete="off"
              onChange={(e) => setBlockTerminal(e.currentTarget.value)}
            />
          </label>

          <label className="lm-field">
            <span className="lm-field__label">{t('shopSetup.blockSeriesLabel')}</span>
            <input
              className="lm-input"
              dir="ltr"
              value={blockSeries}
              autoComplete="off"
              onChange={(e) => setBlockSeries(e.currentTarget.value)}
            />
          </label>

          <label className="lm-field">
            <span className="lm-field__label">{t('shopSetup.blockYearLabel')}</span>
            <input
              className="lm-input"
              dir="ltr"
              inputMode="numeric"
              value={blockYear}
              autoComplete="off"
              onChange={(e) => setBlockYear(e.currentTarget.value)}
            />
          </label>

          <label className="lm-field">
            <span className="lm-field__label">{t('shopSetup.blockSizeLabel')}</span>
            <input
              className="lm-input"
              dir="ltr"
              inputMode="numeric"
              value={blockSize}
              autoComplete="off"
              onChange={(e) => setBlockSize(e.currentTarget.value)}
            />
          </label>

          <button
            type="button"
            className="lm-button"
            disabled={writeId !== null}
            onClick={() => {
              void allocateBlock()
            }}
          >
            {t('shopSetup.allocateBlock')}
          </button>
        </section>
      ) : null}

      {/* `BR-04`, `BR-10`, `REF-010`. Nothing in the product could create a stock location, so
          every tenant on the deployment held zero and could not receive a delivery at all. */}
      <StockLocations
        branchId={branchId}
        canEdit={canOpenBranch}
        loadLocations={loadLocations}
      />

      {error === null ? null : <p className="lm-error">{error}</p>}
      {done === null ? null : <p className="lm-setup-panel__effects">{done}</p>}
      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}
    </section>
  )
}
